import { RefObject, useEffect, useState } from 'react'

/** a drag or a fling settles before the set of bubbles on screen changes */
const SETTLE_MS = 300

const NONE: string[] = []

/**
 * The ids of the rail's bubbles on screen (at least half in the row's view),
 * in the rail's order: the ones a tap can reach next. `order` is the rail's
 * order, so a reordered row is observed again.
 */
export const useVisibleStories = (
  sectionRef: RefObject<HTMLElement>,
  order: unknown,
  enabled: boolean
) => {
  const [visible, setVisible] = useState(NONE)

  useEffect(() => {
    const row = sectionRef.current?.querySelector('ul')
    if (!enabled || !row || typeof IntersectionObserver === 'undefined') {
      setVisible(NONE)
      return undefined
    }
    const items = () =>
      Array.from(row.querySelectorAll<HTMLElement>('li[data-story]'))
    const shown = new Set<string>()
    let timer: ReturnType<typeof setTimeout> | null = null
    const publish = () => {
      timer = null
      const next = items()
        .map(li => li.dataset.story ?? '')
        .filter(id => shown.has(id))
      setVisible(prev =>
        prev.length === next.length && prev.every((id, i) => id === next[i])
          ? prev
          : next
      )
    }
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const id = (entry.target as HTMLElement).dataset.story
          if (id && entry.isIntersecting) {
            shown.add(id)
          } else if (id) {
            shown.delete(id)
          }
        })
        if (!timer) {
          timer = setTimeout(publish, SETTLE_MS)
        }
      },
      { root: row, threshold: 0.5 }
    )
    items().forEach(li => observer.observe(li))
    return () => {
      observer.disconnect()
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [sectionRef, order, enabled])

  return visible
}
